import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { lcApiBase, lcHttpError, lcStationId } from './logiciel-caisse-api.helper';
import { LogicielCaisseAuthService } from './logiciel-caisse-auth.service';

export interface CaisseTableRow {
  id: string;
  CAISSE: string;
  UUID: string;
  ENTREPOT: string;
  VENDEUR: string;
  STATUT: string;
  /** active | inactive (pas d'UUID) | archive */
  _caisseStatutRaw?: string;
  entrepot_id?: number | null;
  numero_de_caisse?: number | null;
}

export interface ParametrageCatalogItem {
  id: number;
  libelle: string;
}

export interface CaisseParametrageRow {
  parametrage_id: number;
  libelle: string;
  is_active: boolean;
}

export interface CaisseParametrePayload {
  parametrage_id: number;
  is_active: boolean;
}

interface CaissesListResponse {
  success: boolean;
  items?: CaisseTableRow[];
  error?: string;
}

interface ParametragesListResponse {
  success: boolean;
  items?: ParametrageCatalogItem[];
  error?: string;
}

interface CaisseParametresResponse {
  success: boolean;
  items?: CaisseParametrageRow[];
  error?: string;
}

@Injectable({ providedIn: 'root' })
export class LogicielCaisseCaisseService {
  constructor(
    private readonly http: HttpClient,
    private readonly authService: LogicielCaisseAuthService
  ) {}

  /** GET /api/entrepots/caisse/list/station/:station_id */
  getCaissesTableRows(): Observable<CaisseTableRow[]> {
    let stationId: number;
    try {
      stationId = lcStationId(this.authService);
    } catch (e) {
      return throwError(() => (e instanceof Error ? e : new Error(String(e))));
    }
    const url = `${lcApiBase(this.authService)}/api/entrepots/caisse/list/station/${stationId}`;
    return this.http.get<CaissesListResponse>(url).pipe(
      map((res) => {
        if (!res?.success || !Array.isArray(res.items)) {
          throw new Error(res?.error || 'Réponse caisses invalide');
        }
        return res.items;
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur lors du chargement des caisses.'))
      )
    );
  }

  /** GET /api/entrepots/parametrage */
  getParametragesCatalog(): Observable<ParametrageCatalogItem[]> {
    const url = `${lcApiBase(this.authService)}/api/entrepots/parametrage`;
    return this.http.get<ParametragesListResponse>(url).pipe(
      map((res) => {
        if (!res?.success || !Array.isArray(res.items)) {
          throw new Error(res?.error || 'Réponse paramétrages invalide');
        }
        return res.items;
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur lors du chargement des paramétrages.'))
      )
    );
  }

  /** GET /api/entrepots/caisse/:id/parametres */
  getCaisseParametres(caisseId: string | number): Observable<CaisseParametrageRow[]> {
    const url = `${lcApiBase(this.authService)}/api/entrepots/caisse/${encodeURIComponent(String(caisseId))}/parametres`;
    return this.http.get<CaisseParametresResponse>(url).pipe(
      map((res) => {
        if (!res?.success || !Array.isArray(res.items)) {
          throw new Error(res?.error || 'Réponse paramètres caisse invalide');
        }
        return res.items;
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur lors du chargement des paramètres.'))
      )
    );
  }

  /** PUT /api/entrepots/caisse/:id/parametres */
  updateCaisseParametres(
    caisseId: string | number,
    parametres: CaisseParametrePayload[]
  ): Observable<void> {
    const url = `${lcApiBase(this.authService)}/api/entrepots/caisse/${encodeURIComponent(String(caisseId))}/parametres`;
    return this.http.put<{ success: boolean; error?: string }>(url, { parametres }).pipe(
      map((res) => {
        if (!res?.success) {
          throw new Error(res?.error || 'Enregistrement impossible');
        }
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, "Erreur lors de l'enregistrement."))
      )
    );
  }

  /** POST /api/entrepots/caisse */
  createCaisse(payload: {
    entrepot_id: number;
    parametres?: CaisseParametrePayload[];
    numero_de_caisse?: number;
  }): Observable<void> {
    const url = `${lcApiBase(this.authService)}/api/entrepots/caisse`;
    return this.http.post<{ success: boolean; error?: string }>(url, payload).pipe(
      map((res) => {
        if (!res?.success) {
          throw new Error(res?.error || 'Création caisse impossible');
        }
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur lors de la création de la caisse.'))
      )
    );
  }

  updateCaisse(
    id: string | number,
    body: { entrepot_id?: number; numero_de_caisse?: number }
  ): Observable<void> {
    const url = `${lcApiBase(this.authService)}/api/entrepots/caisse/${encodeURIComponent(String(id))}`;
    return this.http.put<{ success: boolean; error?: string }>(url, body).pipe(
      map((res) => {
        if (!res?.success) {
          throw new Error(res?.error || 'Mise à jour impossible');
        }
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur lors de la mise à jour de la caisse.'))
      )
    );
  }

  archiveCaisse(id: string | number): Observable<void> {
    const url = `${lcApiBase(this.authService)}/api/entrepots/caisse/${encodeURIComponent(String(id))}/archive`;
    return this.http.patch<{ success: boolean; error?: string }>(url, {}).pipe(
      map((res) => {
        if (!res?.success) {
          throw new Error(res?.error || 'Archivage impossible');
        }
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, "Erreur lors de l'archivage de la caisse."))
      )
    );
  }

  /** Prochain numéro de caisse pour un entrepôt (max existant + 1). */
  static suggestNextNumero(entrepotId: number, rows: CaisseTableRow[]): number {
    const nums = rows
      .filter(
        (r) =>
          Number(r.entrepot_id) === entrepotId &&
          r._caisseStatutRaw !== 'archive'
      )
      .map((r) => Number(r.numero_de_caisse))
      .filter((n) => Number.isFinite(n) && n > 0);
    return nums.length > 0 ? Math.max(...nums) + 1 : 1;
  }
}
